import { useTranslation } from "@vixi/localization";
import { ComponentProps, CSSProperties, forwardRef } from "react";
import { LangKey } from "@vixi/localization";
import { Divider } from "../base/divider";

interface FormHeadingProps extends ComponentProps<"div"> {
  heading: LangKey;
  showDivider?: boolean;
  dividerWidth?: CSSProperties["width"];
  children?: React.ReactNode;
  step?: number;
  totalSteps?: number;
}

/**
 * FormHeading component is used to display a heading with an optional step indicator and a divider.
 * It is styled using Material-UI and supports localization.
 * @param {string} props.heading - DictionaryKey for the heading text
 * @param {number} props.step - Current step number (optional)
 * @param {number} props.totalSteps - Total number of steps (optional)
 * @param {boolean} props.showDivider - Flag to show or hide the divider (optional, default: true)
 * @param {React.ReactNode} props.children - Additional content to be displayed below the heading, but before the divider (optional)
 */
export const FormHeading = forwardRef<HTMLHeadingElement, FormHeadingProps>(
  ({ heading, step, totalSteps, children, showDivider, dividerWidth, ...props }, ref) => {
    const { t, tNum } = useTranslation();

    return (
      <div className=" px-[33.5px] mb-[21px] " {...props} ref={ref}>
        <h2 className={`mt-0 flex items-center text-[25px] font-semibold ${children ? 'mt-3.5 mb-3.5 ' : 'mb-0'}`}>
          {t(heading)}
          &nbsp;&nbsp;&nbsp;
          {totalSteps && (
            <span className="text-[18px]">
              {tNum(step ?? 1)}/{tNum(totalSteps)}
            </span>
          )}
        </h2>

        {children}

      </div>
    );
  },
);

export default FormHeading;
